import React, { useState } from 'react';
import { Box, Typography, Tooltip, Modal, IconButton } from '@mui/material';
import { Close, PlayArrow } from '@mui/icons-material';
import CustomCsPcInput from "../../dms_common/CustomCsPcInput";
import { formatDecimal } from "../../dms_common/DmsUtils";
import { truncateText } from "../../../features/stocks/RealTimeStock/RealTimeStockUtils";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
interface ClaimDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimType: string;
  status: string;
  claimId: string;
  typeOfExpense: string;
  data?: any;
  statusConfig: any;
  caseUnitLabel?: string;
  otherUnitLabel?: string;
  packUnitLabel?: string;
  approvalConfig?: any;
}

const styles = {
  heading: { fontWeight: 600, fontSize: '16px', color: '#464646' },
  box: { backgroundColor: '#F8F8F8', padding: '8px', marginTop: '10px', borderRadius: '8px' },
  remark: { fontSize: '12px', color: '#464646' },
  infoBox: { marginTop: '12px', marginBottom: '12px', display: 'flex', gap: '50px', fontSize: '14px' },
  label: { color: '#909090' },
  value: { color: '#464646' },
  batchBox: {
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '4px',
    marginTop: '10px',
    marginBottom: '0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    maxHeight: '200px',
    overflowY: 'auto'
  },
  batchHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '10px',
    fontSize: '12px',
    color: '#7C7C7C',
    paddingTop: '10px',
    paddingLeft: '10px',
    paddingRight: '10px'
  },
  csPcContainer: {
    display: 'flex',
    gap: '6px',
    width: '48%',
  },
  batchIdColumn: {
    width: '100px',
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
    color: '#A5762A',
    justifyContent: 'flex-end',
  },
  accordionContent: {
    marginTop: '0',
    padding: '10px',
    backgroundColor: 'white',
    borderRadius: '4px',
    maxHeight: '300px',
    overflowY: 'auto',
    width: '100%',
  },
  reasonRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    width: '100%',
  },
  reasonBox: {
    width: 'calc(50% - 5px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingBottom: '14px',
  },
  reasonInputRow: {
    display: 'flex',
    gap: '10px',
  },
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Legend = () => {
  return (
    <Box sx={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '10px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box sx={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#17B4A3' }} />
        <Typography variant="body2" sx={{ fontSize: '12px', color: '#464646' }}>
          Approved
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box sx={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#CC911F' }} />
        <Typography variant="body2" sx={{ fontSize: '12px', color: '#464646' }}>
          Partially Approved
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box sx={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#C44C36' }} />
        <Typography variant="body2" sx={{ fontSize: '12px', color: '#464646' }}>
          Rejected
        </Typography>
      </Box>
    </Box>
  );
};

const ClaimDetailsModal: React.FC<ClaimDetailsModalProps> = ({
  isOpen,
  onClose,
  claimType,
  status,
  claimId,
  typeOfExpense,
  data,
  statusConfig,
  caseUnitLabel,
  otherUnitLabel,
  packUnitLabel,
  approvalConfig
}) => {
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const [expandedInvoiceIndex, setExpandedInvoiceIndex] = useState<number | null>(null);

  const toggleAccordion = (batchId: string) => {
    setOpenBatchId(openBatchId === batchId ? null : batchId);
  };

  const toggleInvoiceExpansion = (index: number) => {
    setExpandedInvoiceIndex(expandedInvoiceIndex === index ? null : index);
  };

  const updatedStatusConfig = statusConfig.map((config: any) => ({
    ...config,
    id: config.id?.toUpperCase(),
  }));
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { claimTypes, claimSubTypes, currency } = ConfigDictionary;

  const getStatusColors = (status: string, item: any) => {
    const isApproved = item.approvedCs > 0 || item.approvedPc > 0 || item.approvedOther > 0 ||
      item.approvedCases > 0 || item.approvedPcs > 0 || item.approvedOthers > 0;
    const isRejected = !isApproved && (status === updatedStatusConfig[2].id ||
      (status === updatedStatusConfig[3].id && !isApproved));

    if (status === updatedStatusConfig[0].id) {
      return { borderColor: "#17B4A3", backgroundColor: "#FAFFFF" };
    } else if (isRejected) {
      return { borderColor: "#C44C36", backgroundColor: "#FFFAFA" };
    } else if (status === updatedStatusConfig[3].id) {
      return {
        borderColor: isApproved ? "#17B4A3" : "#C44C36",
        backgroundColor: isApproved ? "#FAFFFF" : "#FFFAFA"
      };
    } else if (status === updatedStatusConfig[1].id) {
      return { borderColor: "#D9D9D9", backgroundColor: "#FFFFFF" };
    }

    return { borderColor: "#D9D9D9", backgroundColor: "#FFFFFF" };
  };

  const renderContent = () => {
    if (claimType === claimTypes[0]) {
      return (
        <>
          <Box sx={styles.box}>
            <Box sx={styles.batchHeader}>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C' }}>
                {data.batchIds.length} {data.batchIds.length === 1 ? 'Batch ID' : 'Batch IDs'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C', paddingLeft: (status === updatedStatusConfig[3].id || status === updatedStatusConfig[2].id) ? '14%' : '30%' }}>
                Damage Qty.
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C', paddingLeft: (status === updatedStatusConfig[3].id || status === updatedStatusConfig[2].id) ? '22.8%' : '23%' }}>
                Approved Qty.
              </Typography>
              {status === updatedStatusConfig[3].id && (
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C' }}>
                </Typography>
              )}
            </Box>
            <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
              {data.batchIds.map((batch: any, index: number) => {
                const { borderColor, backgroundColor } = getStatusColors(status, batch);

                return (
                  <Box key={index}>
                    <Box sx={styles.batchBox}>
                      <Typography variant="body2" sx={{ color: '#7C7C7C', ...styles.batchIdColumn }}>
                        {batch.batchId}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: '10px' }}>
                        <Box sx={{ ...styles.csPcContainer, width: '270px' }}>
                          {caseUnitLabel && (
                            <CustomCsPcInput
                              value={batch.claimableCs}
                              onChange={() => null}
                              label={caseUnitLabel}
                              percentage={90}
                              borderWidth="0.5px"
                              readOnly={true}
                            />
                          )}
                          {otherUnitLabel && (
                            <CustomCsPcInput
                              value={batch.claimableOther}
                              onChange={() => null}
                              label={otherUnitLabel}
                              percentage={90}
                              borderWidth="0.5px"
                              readOnly={true}
                            />
                          )}
                          {packUnitLabel && (
                            <CustomCsPcInput
                              value={batch.claimablePc}
                              onChange={() => null}
                              label={packUnitLabel}
                              percentage={90}
                              borderWidth="0.5px"
                              readOnly={true}
                            />
                          )}
                        </Box>
                        <Box sx={{ ...styles.csPcContainer, width: '270px' }}>
                          {caseUnitLabel && (
                            <CustomCsPcInput
                              value={batch.approvedCs}
                              onChange={() => null}
                              label={caseUnitLabel}
                              borderColor={borderColor}
                              backgroundColor={backgroundColor}
                              percentage={90}
                              borderWidth="0.5px"
                              readOnly
                            />
                          )}
                          {otherUnitLabel && (
                            <CustomCsPcInput
                              value={batch.approvedOther}
                              onChange={() => null}
                              label={otherUnitLabel}
                              borderColor={borderColor}
                              backgroundColor={backgroundColor}
                              percentage={90}
                              borderWidth="0.5px"
                              readOnly={true}
                            />
                          )}
                          {packUnitLabel && (
                            <CustomCsPcInput
                              value={batch.approvedPc}
                              onChange={() => null}
                              label={packUnitLabel}
                              borderColor={borderColor}
                              backgroundColor={backgroundColor}
                              percentage={90}
                              borderWidth="0.5px"
                              readOnly={true}
                            />
                          )}
                        </Box>
                      </Box>
                      {(status === updatedStatusConfig[3].id || status === updatedStatusConfig[2].id) && (
                        <>
                          {!(
                            batch.claimableCs === batch.approvedCs &&
                            batch.claimablePc === batch.approvedPc &&
                            batch.claimableOther === batch.approvedOther
                          ) ? (
                            <Box sx={styles.viewButton} onClick={() => toggleAccordion(batch.batchId)}>
                              <Typography variant="body2" sx={{ color: '#A5762A' }}>
                                View
                              </Typography>
                              <PlayArrow
                                sx={{
                                  height: '15px',
                                  width: '15px',
                                  transform: openBatchId === batch.batchId ? 'rotate(-90deg)' : 'rotate(90deg)',
                                }}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ width: '62px' }} />
                          )}
                        </>
                      )}
                    </Box>

                    {(status === updatedStatusConfig[3].id || status === updatedStatusConfig[2].id) && openBatchId === batch.batchId && (
                      <Box sx={styles.accordionContent}>
                        <Box sx={styles.reasonRow}>
                          {batch.returnReasonArray.map((reason: any, reasonIndex: number) => (
                            <Box key={reasonIndex} sx={styles.reasonBox}>
                              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C' }}>
                                {reason.reason}
                              </Typography>
                              <Box sx={styles.reasonInputRow}>
                                <Box sx={styles.csPcContainer}>
                                  {caseUnitLabel && (
                                    <CustomCsPcInput
                                      value={reason.cs}
                                      onChange={() => null}
                                      label={caseUnitLabel}
                                      percentage={90}
                                      borderWidth="0.5px"
                                      readOnly={true}
                                    />
                                  )}
                                  {otherUnitLabel && (
                                    <CustomCsPcInput
                                      value={reason.other}
                                      onChange={() => null}
                                      label={otherUnitLabel}
                                      percentage={90}
                                      borderWidth="0.5px"
                                      readOnly={true}
                                    />
                                  )}
                                  {packUnitLabel && (
                                    <CustomCsPcInput
                                      value={reason.pc}
                                      onChange={() => null}
                                      label={packUnitLabel}
                                      percentage={90}
                                      borderWidth="0.5px"
                                      readOnly={true}
                                    />
                                  )}
                                </Box>
                                <Box sx={styles.csPcContainer}>
                                  {caseUnitLabel && (
                                    <CustomCsPcInput
                                      value={reason.approvedCs}
                                      onChange={() => null}
                                      label={caseUnitLabel}
                                      borderColor={
                                        reason.approvedCs === 0
                                          ? "#C44C36"
                                          : reason.cs === reason.approvedCs
                                            ? "#17B4A3"
                                            : "#CC911F"
                                      }
                                      percentage={90}
                                      borderWidth="0.5px"
                                      readOnly={true}
                                    />
                                  )}
                                  {otherUnitLabel && (
                                    <CustomCsPcInput
                                      value={reason.approvedOther}
                                      onChange={() => null}
                                      label={otherUnitLabel}
                                      borderColor={
                                        reason.approvedOther === 0
                                          ? "#C44C36"
                                          : reason.other === reason.approvedOther
                                            ? "#17B4A3"
                                            : "#CC911F"
                                      }
                                      percentage={90}
                                      borderWidth="0.5px"
                                      readOnly={true}
                                    />
                                  )}
                                  {packUnitLabel && (
                                    <CustomCsPcInput
                                      value={reason.approvedPc}
                                      onChange={() => null}
                                      label={packUnitLabel}
                                      borderColor={
                                        reason.approvedPc === 0
                                          ? "#C44C36"
                                          : reason.pc === reason.approvedPc
                                            ? "#17B4A3"
                                            : "#CC911F"
                                      }
                                      percentage={90}
                                      borderWidth="0.5px"
                                      readOnly={true}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ ...styles.box, marginTop: '0px' }}>
                          <Typography variant="body2" sx={styles.remark}>
                            {batch.rejectionReason}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      );
    }
    if (typeOfExpense === claimSubTypes[2]) {
      return (
        <>
          <Box sx={styles.box}>
            <Box sx={styles.batchHeader}>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C' }}>
                {data.invoiceNo} {data.invoiceNo === 1 ? 'Invoice' : 'Invoices'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C', paddingLeft: data.invoiceNo === 1 ? '71%' : '70%' }}>
                Claim Amount
              </Typography>
            </Box>
            <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
              {data.invoices.map((invoice: any, index: number) => {
                const isApproved = invoice.approvedCs > 0 || invoice.approvedPc > 0 || invoice.approvedOther > 0;
                const isRejected = !isApproved && (status === updatedStatusConfig[2].id ||
                  (status === updatedStatusConfig[3].id && !isApproved));
                const { borderColor, backgroundColor } = getStatusColors(status, invoice);
                let textColor = '#464646';

                return (
                  <Box key={index}>
                    <Box sx={styles.batchBox}>
                      <Typography variant="body2" sx={{ color: '#7C7C7C', width: '100%' }}>
                        {invoice.invoiceNumber}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: '10px', width: '260px', alignItems: 'center' }}>
                        <Box sx={{
                          ...styles.csPcContainer,
                          width: '120px',
                          padding: '4px',
                          border: `1px solid ${borderColor}`,
                          borderRadius: '6px',
                          color: textColor
                        }}>
                          {`${currency} ${formatDecimal(invoice.discount)}`}
                        </Box>

                        {isRejected && invoice.rejectionReason && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => toggleInvoiceExpansion(index)}
                          >
                            <Typography variant="caption" sx={{ color: '#A5762A', marginRight: '4px' }}>
                              Reason
                            </Typography>
                            {expandedInvoiceIndex === index ? <ExpandLess sx={{ color: '#A5762A' }} /> : <ExpandMore sx={{ color: '#A5762A' }} />}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {expandedInvoiceIndex === index && invoice.rejectionReason && (
                      <Box sx={{
                        backgroundColor: '#FFFFFF',
                        padding: '12px',
                        borderRadius: '4px',
                      }}>
                        <Typography
                          variant="body2"
                          sx={{
                            backgroundColor: '#F8F8F8',
                            padding: '8px',
                            borderRadius: '4px',
                          }}
                        >
                          {invoice.rejectionReason}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      );
    }
    if (typeOfExpense === claimSubTypes[3]) {
      const { borderColor, backgroundColor } = getStatusColors(status, data);

      return (
        <>
          <Box sx={styles.box}>
            <Box sx={styles.batchHeader}>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C' }}>
                Invoice No.
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#7C7C7C', paddingLeft: '78%' }}>
                Claim Amount
              </Typography>
            </Box>
            <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
              <Box>
                <Box sx={styles.batchBox}>
                  <Typography variant="body2" sx={{ color: '#7C7C7C', width: '100%' }}>
                    {data.returnOrderNo || data.invoiceNumber}
                  </Typography>
                  <Box sx={{
                    ...styles.csPcContainer,
                    width: '145px',
                    padding: '4px',
                    border: `1px solid ${borderColor}`,
                    backgroundColor: backgroundColor,
                    borderRadius: '6px',
                    color: '#464646'
                  }}>
                    {data.claimAmount}
                  </Box>
                </Box>
              </Box>
              {data.approvals?.length > 0 && (
                <>
                  {data.approvals.some((approval: any) => approval.status !== 'PENDING') && (
                    <Typography variant="body2" sx={{
                      fontSize: '14px',
                      color: '#444444',
                      fontWeight: 500,
                      marginTop: '10px',
                      marginBottom: '10px',
                      marginLeft: '8px'
                    }}>
                      Approved Amt by
                    </Typography>
                  )}
                  <Box sx={{
                    padding: '12px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '4px'
                  }}>

                    {data.approvals
                      .filter((approval: any) => approval.status !== 'PENDING')
                      .reduce((acc: any[], approval: any) => {
                        const existing = acc.find(
                          (item) =>
                            item.designation === approval.designation &&
                            item.userId === approval.userId
                        );
                        if (existing) {
                          existing.approvedAmount = formatDecimal(parseFloat(existing.approvedAmount) + parseFloat(approval.approvedAmount));
                        } else {
                          acc.push({ ...approval });
                        }
                        return acc;
                      }, [])
                      .sort((a: { designation: string }, b: { designation: string }) => {
                        const aLevel = approvalConfig?.flow.findIndex((level: any) =>
                          level.includes(a.designation))
                        const bLevel = approvalConfig?.flow.findIndex((level: any) =>
                          level.includes(b.designation))
                        return bLevel - aLevel;
                      })
                      .map((approval: any, aIndex: number) => {
                        const isApproved = approval.status === 'APPROVED';
                        const isRejected = approval.status === 'REJECTED';
                        const borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";
                        const backgroundColor = isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF";

                        return (
                          <Box key={`approval-${aIndex}`}>
                            <Box sx={{ ...styles.batchBox, marginTop: '0px', padding: '10px 0px' }}>
                              <Typography variant="body2" sx={{
                                fontSize: '13px',
                                fontWeight: 400
                              }}>
                                {approval.designation || 'Unknown'}
                              </Typography>
                              <Box sx={{
                                ...styles.csPcContainer,
                                width: '120px',
                                padding: '4px',
                                border: `1px solid ${borderColor}`,
                                backgroundColor: `${backgroundColor}`,
                                borderRadius: '6px',
                                color: '#464646'
                              }}>
                                {data.claimAmount}
                              </Box>
                            </Box>
                            {approval.status === 'REJECTED' && (
                              <Box sx={{
                                backgroundColor: '#fdecea',
                                padding: '8px',
                                borderRadius: '4px'
                              }}>
                                <Typography variant="caption" sx={{
                                  color: '#c44c36',
                                  display: 'block',
                                  marginBottom: '4px',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                }}>
                                  Rejection Reason
                                </Typography>
                                <Typography variant="body2" sx={{
                                  color: '#7C7C7C',
                                  fontSize: '12px'
                                }}>
                                  {approval.reason || 'No reason provided'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </>
      );
    }
    return (
      <>
        {typeOfExpense === claimSubTypes[4] ? (
          <>
            <Typography variant="body2" sx={{ ...styles.label, marginBottom: '4px' }}>
              Remark
            </Typography>
            <Box sx={styles.box}>
              <Typography variant="body2" sx={styles.remark}>
                {data.remark}
              </Typography>
            </Box>

            {(status === updatedStatusConfig[2].id || status === updatedStatusConfig[3].id) && data.rejectionReason && (
              <>
                <Typography variant="body2" sx={{ ...styles.label, marginTop: '16px', marginBottom: '4px', color: '#C44C36' }}>
                  Rejection Reason
                </Typography>
                <Box sx={styles.box}>
                  <Typography variant="body2" sx={styles.remark}>
                    {data.rejectionReason}
                  </Typography>
                </Box>
              </>
            )}
          </>
        ) : typeOfExpense === claimSubTypes[5] ? (
          <>
            <Box sx={styles.infoBox}>
              <Typography variant="body2" sx={styles.label}>
                Installation Date: <span style={styles.value}>{formatDate(data.displayStartDate)}</span>
              </Typography>
              <Typography variant="body2" sx={styles.label}>
                Installation Cost: <span style={styles.value}>{data.claimAmount}</span>
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ ...styles.label, marginTop: '16px', marginBottom: '4px' }}>
              Remark
            </Typography>
            <Box sx={styles.box}>
              <Typography variant="body2" sx={styles.remark}>
                {data.remark}
              </Typography>
            </Box>

            {(status === updatedStatusConfig[2].id || status === updatedStatusConfig[3].id) && data.rejectionReason && (
              <>
                <Typography variant="body2" sx={{ ...styles.label, marginTop: '16px', marginBottom: '4px', color: '#C44C36' }}>
                  Rejection Reason
                </Typography>
                <Box sx={styles.box}>
                  <Typography variant="body2" sx={styles.remark}>
                    {data.rejectionReason}
                  </Typography>
                </Box>
              </>
            )}
          </>
        ) : null}
      </>
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "950px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          width: '100%'
        }}>
          {typeOfExpense === claimSubTypes[3] ? (
            <Typography variant="h6" sx={styles.heading}>
              Claim ID. {claimId}
            </Typography>
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              gap: '16px'
            }}>
              {(typeOfExpense === claimSubTypes[4] || typeOfExpense === claimSubTypes[5]) && (
                <Typography variant="h6" sx={{ ...styles.heading, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {data.outletName} ({data.outletCode})
                </Typography>
              )}
              {(claimType === claimTypes[0] || (typeOfExpense === claimSubTypes[2])) && (
                <Tooltip title={`${data.skuName} (${data.skuCode})`} arrow>
                  <Typography variant="h6" sx={{ ...styles.heading, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {truncateText(data.skuName, 50)} ({truncateText(data.skuCode, 50)})
                  </Typography>
                </Tooltip>
              )}
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            position: 'relative',
          }}>
            {(status === updatedStatusConfig[3].id && !(typeOfExpense === claimSubTypes[2] || typeOfExpense === claimSubTypes[3] || typeOfExpense === claimSubTypes[4] || typeOfExpense === claimSubTypes[5])) && (
              <Legend />
            )}
            <IconButton
              onClick={onClose}
              sx={{
                color: "#252525",
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
        {renderContent()}
      </Box>
    </Modal>
  );
};

export default ClaimDetailsModal;